import { useDispatch } from 'react-redux';
import { apiClient } from '../../utils/axios';
import { printPdf } from '../../helper/PrintPdf';
import { showSnackbar } from '../../api/snackbarSlice';
import { useTranslatedMessages } from '../../hooks/useTranslatedMessages';

export const usePdfActions = () => {
  const dispatch = useDispatch();
  const { snackbarMessages } = useTranslatedMessages();
  const pdfMessages = snackbarMessages.document_pdf ?? {};

  const downloadPdf = async ({ url, fileName }) => {
    dispatch(showSnackbar({ message: pdfMessages.downloading_document, severity: 'info' }));

    try {
      const response = await apiClient.get(url, {
        responseType: 'blob', // important for binary data
        headers: {
          Accept: 'application/pdf',
        },
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);

      const xFilename = response.headers?.['x-suggested-filename'];
      const finalFileName = xFilename ?? fileName;

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', finalFileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(downloadUrl);
      dispatch(showSnackbar({ message: pdfMessages.document_download_success, severity: 'success' }));
    } catch (error) {
      dispatch(showSnackbar({ message: pdfMessages.document_download_failed, severity: 'error' }));
      console.error(error);
    }
  };

  const handlePrintPdf = async ({ url, fileName }) => {
    try {
      const response = await apiClient.get(url, {
        responseType: 'blob',
        headers: { Accept: 'application/pdf' },
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const xFilename = response.headers?.['x-suggested-filename'];
      const finalFileName = xFilename ?? fileName ?? 'document.pdf';
      const printWindow = window.open(URL.createObjectURL(blob), '_blank');
      if (!printWindow) {
        dispatch(showSnackbar({ message: pdfMessages.print_window_unavailable, severity: 'error' }));
        return;
      }

      printWindow.onload = () => {
        printWindow.document.title = finalFileName;
        printWindow.focus();
        printWindow.print();
      };
      dispatch(showSnackbar({ message: pdfMessages.document_print_success, severity: 'success' }));
      return true;
    } catch (error) {
      dispatch(showSnackbar({ message: pdfMessages.document_print_failed, severity: 'error' }));
      console.error(error);
      return false;
    }
  };

  return { downloadPdf, handlePrintPdf };
};
